import { Logger } from '@frontend/logger';
import { useEffect, useRef, useState } from 'react';
import ScanbotSDK from 'scanbot-web-sdk';
import type { BarcodeScannerViewConfiguration, IBarcodeScannerHandle } from 'scanbot-web-sdk/@types';
import { BarcodeScannerResultWithSize } from 'scanbot-web-sdk/@types/model/barcode/barcode-result';

import { useScanbotSDKProvider } from '../scanbotSDKProvider';
import { ScanbotScannerProps } from './scanbot-scanner.component';

interface ViewProps {
    divRef: React.RefObject<HTMLDivElement | null>;
    isLoading: boolean;
    resultType: 'success' | 'error' | null;
    sdkLoading: boolean;
}

const SCANNER_ID = 'scanbot-scanner-video';

const useScanbotScanner = (props: ScanbotScannerProps): ViewProps => {
    const { scanbotSDK, validLicense, sdkLoading } = useScanbotSDKProvider();
    const [scanner, setScanner] = useState<IBarcodeScannerHandle | null>(null);
    const divRef = useRef<HTMLDivElement | null>(null);
    const [resultType, setResultType] = useState<'success' | 'error' | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const callbackRef = useRef(props.callback);
    callbackRef.current = props.callback;

    useEffect(() => {
        if (scanbotSDK && divRef.current && validLicense) {
            const configuration: BarcodeScannerViewConfiguration = {
                containerId: SCANNER_ID,
                onBarcodesDetected: (result: BarcodeScannerResultWithSize) => {
                    const text = result.barcodes[0].text;
                    if (callbackRef.current) {
                        const result = callbackRef.current(text);
                        if (result instanceof Promise) {
                            setIsLoading(true);
                            result
                                .then(() => {
                                    setResultType('success');
                                })
                                .catch(() => {
                                    setResultType('error');
                                });
                        }
                    }
                },
                scannerConfiguration: {
                    barcodeFormatConfigurations: [
                        new ScanbotSDK.Config.BarcodeFormatCommonConfiguration({ formats: props.formats || ['QR_CODE', 'CODE_128', 'DATA_MATRIX'] })
                    ]
                },
                finder: {
                    _type: 'ViewFinderConfiguration',
                    visible: true,
                    aspectRatio: {
                        width: divRef.current.offsetWidth,
                        height: divRef.current.offsetHeight
                    }
                },
                userGuidance: {
                    visible: false
                }
            };
            scanbotSDK.createBarcodeScanner(configuration).then(setScanner);
        }

        return () => {
            closeScanner(scanner);
        };
    }, [scanbotSDK, divRef, validLicense]);

    useEffect(() => {
        if (resultType) {
            const timeout = setTimeout(() => {
                setResultType(null);
                setIsLoading(false);
            }, 150);
            return () => clearTimeout(timeout);
        }
        return;
    }, [resultType]);

    useEffect(() => {
        // Always call through callbackRef so e2e mockScan / scanner-mock-event hit the
        // latest parent callback (empty deps otherwise freeze the first render's props).
        const mockScan = (code: string) => {
            Logger.log('Scanned value:', code, '<<<');
            callbackRef.current?.(code);
        };
        (window as any).mockScan = mockScan;
        const handler = (e: any) => mockScan(e.detail);
        window.addEventListener('scanner-mock-event', handler);
        return () => {
            delete (window as any).mockScan;
            window.removeEventListener('scanner-mock-event', handler);
        };
    }, []);

    useEffect(() => {
        return () => {
            closeScanner(scanner);
        };
    }, [scanner]);

    const closeScanner = (scanner: IBarcodeScannerHandle | null) => {
        if (scanner == null) return;
        const activeCameraInfo = scanner.getActiveCameraInfo();
        if (activeCameraInfo) {
            navigator.mediaDevices.getUserMedia({ video: { deviceId: activeCameraInfo.deviceId } }).then((stream) => {
                stream.getTracks().forEach((track) => track.stop());
            });
        }
        scanner.dispose();
        setScanner(null);
    };

    return {
        divRef,
        isLoading,
        resultType,
        sdkLoading
    };
};

export default useScanbotScanner;
